/**
 * ¿El teléfono se acuerda de algo entre una apertura y la siguiente?
 *
 * ## Por qué hace falta preguntarlo
 *
 * Todo el modo sin conexión descansa sobre `localStorage`: ahí vive la
 * credencial que deja abrir la aplicación sin señal, y ahí guarda Supabase su
 * propia sesión. Si ese almacén no persiste, nada de eso funciona —y **falla en
 * silencio**, porque escribir en un almacén que no guarda no tira ningún error:
 * se escribe, se lee bien dentro de la misma sesión, y al volver a abrir no hay
 * nada.
 *
 * Eso es exactamente lo que se vio en el campo: entrar con señal, cerrar la
 * aplicación, abrirla en modo avión, y que pidiera contraseña como si nunca se
 * hubiera entrado. Cuatro versiones buscándolo del lado del login, que estaba
 * bien.
 *
 * ## Qué mide, y qué NO puede medir
 *
 * Dos cosas distintas, porque se rompen distinto:
 *
 *  - **`escribe`**: se guarda un valor y se vuelve a leer, en el acto. Si esto
 *    falla, el almacén está bloqueado —modo privado, almacenamiento
 *    deshabilitado en el WebView, cuota llena—.
 *  - **`veniaDeAntes`**: había una marca de una apertura anterior. Si `escribe`
 *    anda y esto no, el almacén acepta lo que se le da y lo tira al cerrar.
 *
 * En la PRIMERA apertura de la vida de una instalación, `veniaDeAntes` es
 * `false` legítimamente y no se puede distinguir del caso malo. Es una sola vez
 * y se prefiere así: un diagnóstico que se calla para no equivocarse una vez no
 * sirve para las otras.
 *
 * La marca se escribe al leerla, no antes: si se escribiera primero, la lectura
 * encontraría siempre lo recién puesto y esto contestaría «ok» para siempre.
 */

const MARCA = 'gv.marcaDeArranque';
const PRUEBA = 'gv.pruebaDeEscritura';

export interface EstadoDelAlmacen {
  /** Se puede escribir y volver a leer dentro de esta sesión. */
  escribe: boolean;
  /** Había una marca de una apertura anterior de la aplicación. */
  veniaDeAntes: boolean;
}

function medir(): EstadoDelAlmacen {
  let escribe = false;
  let veniaDeAntes = false;

  try {
    // Primero LEER la marca vieja. El orden importa: al revés, siempre se
    // encontraría la propia marca recién escrita.
    veniaDeAntes = localStorage.getItem(MARCA) !== null;

    const testigo = String(Date.now());
    localStorage.setItem(PRUEBA, testigo);
    escribe = localStorage.getItem(PRUEBA) === testigo;
    localStorage.removeItem(PRUEBA);

    if (escribe) localStorage.setItem(MARCA, testigo);
  } catch {
    // Almacenamiento bloqueado. `escribe` queda en `false`, que es la
    // respuesta correcta.
  }

  return { escribe, veniaDeAntes };
}

/**
 * Se mide UNA vez, al cargar el módulo, y no en cada llamada.
 *
 * Porque la propia medición escribe la marca: preguntar dos veces en la misma
 * apertura daría `veniaDeAntes: true` la segunda, y esa respuesta sería sobre
 * la marca que puso la primera.
 */
const estado = medir();

export function estadoDelAlmacen(): EstadoDelAlmacen {
  return estado;
}

/** Para pruebas: volver a medir sobre el almacén que haya ahora. */
export function medirDeNuevo(): EstadoDelAlmacen {
  return medir();
}

/**
 * Una línea para mostrar en pantalla, en castellano y sin jerga.
 *
 * Va junto al número de versión, en chico. No es para quien usa la aplicación
 * todos los días; es para el momento en que algo no anda y hay que mirar una
 * foto de la pantalla y saber por dónde empezar.
 */
export function comoEstaElAlmacen(e: EstadoDelAlmacen = estado): string {
  if (!e.escribe) return 'memoria bloqueada';
  if (!e.veniaDeAntes) return 'memoria no recuerda';
  return 'memoria ok';
}
